import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Shield, ArrowLeft, RefreshCw, Activity, AlertTriangle, CheckCircle2,
  Clock, Bomb, CreditCard, Eye, HeartPulse, TrendingUp, MessageSquareWarning,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { MLB_MATCHUP_SABOTAGES } from '@/data/sabotageCatalog';
import { todayET, etWeekStartMonday, addDaysToBareDate } from '@/lib/etDate';

const ADMIN_EMAIL = 'adamneilemerson@gmail.com';

const sabotageRarityMap = new Map<string, string>();
MLB_MATCHUP_SABOTAGES.forEach(s => sabotageRarityMap.set(s.name, s.rarity));

function timeAgo(ts: string | null): string {
  if (!ts) return 'never';
  const diff = Date.now() - new Date(ts).getTime();
  if (diff < 60_000) return `${Math.floor(diff / 1000)}s ago`;
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return `${Math.floor(diff / 86_400_000)}d ago`;
}

function hoursDown(ts: string | null): number {
  if (!ts) return 999;
  return Math.floor((Date.now() - new Date(ts).getTime()) / 3_600_000);
}

function isStale(ts: string | null, minutesThreshold: number): boolean {
  if (!ts) return true;
  return Date.now() - new Date(ts).getTime() > minutesThreshold * 60_000;
}

const todayStr = () => todayET();   // SAB-793: was a local alias for a duplicate impl

// ─── Types ───────────────────────────────────────────────────
interface LeagueHealth {
  id: string;
  name: string;
  lastScoredAt: string | null;
  rowsToday: number;
  rowsThisWeek: number;
  duplicates: number;
}

interface MatchupScore {
  id: string;
  leagueName: string;
  homeTeam: string;
  awayTeam: string;
  homeScore: number;
  awayScore: number;
  homeCalc: number;
  awayCalc: number;
  homeDiff: number;
  awayDiff: number;
  lastScoredAt: string | null;
  discrepancy: boolean;
  affectedSide: 'home' | 'away' | 'both' | 'none';
}

interface SabotageRow {
  id: string;
  leagueName: string;
  matchupLabel: string;
  sabotage_name: string;
  rarity: string;
  effect_config: any;
  appliedBy: string;
  applied_at: string;
  week: number | null;
  league_id: string;
}

interface OutlierRow {
  playerName: string;
  teamName: string;
  leagueName: string;
  gameDate: string;
  totalPoints: number;
  playerId: string;
}

interface CronHealth {
  boxScoresLastRun: string | null;
  boxScoresTodayCount: number;
  gamesTodayCount: number;
  liveOrFinalCount: number;
  matchupEngineLastRun: string | null;
  seasonStatsLastRun: string | null;
}

interface CronImpact {
  unscoredGames: number;
  affectedMatchups: number;
}

interface CreditRow {
  userId: string;
  username: string | null;
  displayName: string | null;
  teamName: string;
  leagueName: string;
}

interface ClientErrorRow {
  id: string;
  message: string;
  page: string | null;
  component: string | null;
  username: string | null;
  displayName: string | null;
  createdAt: string;
  metadata: any;
}

// ─── Main Component ──────────────────────────────────────────
const AdminDashboard = () => {
  const { user, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [scoringHealth, setScoringHealth] = useState<LeagueHealth[]>([]);
  const [matchupScores, setMatchupScores] = useState<MatchupScore[]>([]);
  const [sabotages, setSabotages] = useState<SabotageRow[]>([]);
  const [outliers, setOutliers] = useState<OutlierRow[]>([]);
  const [cronHealth, setCronHealth] = useState<CronHealth>({ boxScoresLastRun: null, boxScoresTodayCount: 0, gamesTodayCount: 0, liveOrFinalCount: 0, matchupEngineLastRun: null, seasonStatsLastRun: null });
  const [cronImpact, setCronImpact] = useState<CronImpact>({ unscoredGames: 0, affectedMatchups: 0 });
  const [creditRows, setCreditRows] = useState<CreditRow[]>([]);
  const [clientErrors, setClientErrors] = useState<ClientErrorRow[]>([]);
  const [grantingId, setGrantingId] = useState<string | null>(null);

  const isAdmin = user?.email === ADMIN_EMAIL;

  const fetchAll = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      await Promise.all([
        fetchScoringHealth(),
        fetchMatchupScores(),
        fetchSabotages(),
        fetchCronHealth(),
        fetchCredits(),
        fetchOutliers(),
        fetchClientErrors(),
      ]);
    } finally {
      setLoading(false);
      setLastRefresh(new Date());
    }
  }, [isAdmin]);

  // ─── 1. SCORING HEALTH ────────────────────────────────────
  const fetchScoringHealth = async () => {
    const { data: leagues } = await supabase.from('leagues').select('id, name');
    if (!leagues) return;

    const td = todayStr();
    const weekStart = etWeekStartMonday();   // SAB-793

    const health: LeagueHealth[] = await Promise.all(
      leagues.map(async (league) => {
        const [latestRes, todayRes, weekRes, allRes] = await Promise.all([
          supabase.from('player_match_scores').select('scored_at').eq('league_id', league.id).order('scored_at', { ascending: false }).limit(1),
          supabase.from('player_match_scores').select('id', { count: 'exact', head: true }).eq('league_id', league.id).eq('game_date', td),
          supabase.from('player_match_scores').select('id', { count: 'exact', head: true }).eq('league_id', league.id).gte('game_date', weekStart),
          supabase.from('player_match_scores').select('player_id, game_date').eq('league_id', league.id).gte('game_date', weekStart),
        ]);

        let duplicates = 0;
        if (allRes.data) {
          const seen = new Map<string, number>();
          allRes.data.forEach(r => {
            const key = `${r.player_id}_${r.game_date}`;
            seen.set(key, (seen.get(key) || 0) + 1);
          });
          seen.forEach(count => { if (count > 1) duplicates++; });
        }

        return {
          id: league.id,
          name: league.name,
          lastScoredAt: latestRes.data?.[0]?.scored_at || null,
          rowsToday: todayRes.count || 0,
          rowsThisWeek: weekRes.count || 0,
          duplicates,
        };
      })
    );
    setScoringHealth(health);
  };

  // ─── 2. MATCHUP SCORES ────────────────────────────────────
  const fetchMatchupScores = async () => {
    const { data: leagues } = await supabase.from('leagues').select('id, name');
    if (!leagues) return;

    const results: MatchupScore[] = [];
    for (const league of leagues) {
      const { data: matchups } = await supabase
        .from('matchups')
        .select('id, home_team_id, home_team_name, away_team_id, away_team_name, home_score, away_score, scored_at, week')
        .eq('league_id', league.id)
        .eq('week', 1);

      if (!matchups) continue;
      for (const m of matchups) {
        const [homeRes, awayRes] = await Promise.all([
          supabase.from('player_match_scores').select('total_points').eq('matchup_id', m.id).eq('team_id', m.home_team_id).eq('is_starter', true),
          supabase.from('player_match_scores').select('total_points').eq('matchup_id', m.id).eq('team_id', m.away_team_id).eq('is_starter', true),
        ]);

        const homeCalc = (homeRes.data || []).reduce((s, r) => s + Number(r.total_points), 0);
        const awayCalc = (awayRes.data || []).reduce((s, r) => s + Number(r.total_points), 0);
        const homeDiff = homeCalc - Number(m.home_score);
        const awayDiff = awayCalc - Number(m.away_score);
        const homeOff = Math.abs(homeDiff) > 0.01;
        const awayOff = Math.abs(awayDiff) > 0.01;

        results.push({
          id: m.id,
          leagueName: league.name,
          homeTeam: m.home_team_name,
          awayTeam: m.away_team_name,
          homeScore: Number(m.home_score),
          awayScore: Number(m.away_score),
          homeCalc, awayCalc, homeDiff, awayDiff,
          lastScoredAt: m.scored_at,
          discrepancy: homeOff || awayOff,
          affectedSide: homeOff && awayOff ? 'both' : homeOff ? 'home' : awayOff ? 'away' : 'none',
        });
      }
    }
    setMatchupScores(results);
  };

  // ─── 3. SABOTAGE STATE ────────────────────────────────────
  const fetchSabotages = async () => {
    const { data: leagues } = await supabase.from('leagues').select('id, name');
    if (!leagues) return;
    const leagueMap = new Map(leagues.map(l => [l.id, l.name]));

    const { data: sabs } = await supabase
      .from('applied_sabotages')
      .select('id, league_id, matchup_id, sabotage_name, effect_config, applied_by_team_id, applied_at, status, week, cost')
      .eq('status', 'active')
      .order('applied_at', { ascending: false });

    if (!sabs) { setSabotages([]); return; }

    const teamIds = [...new Set(sabs.map(s => s.applied_by_team_id))];
    const { data: teams } = await supabase.from('teams').select('id, name').in('id', teamIds);
    const teamMap = new Map((teams || []).map(t => [t.id, t.name]));

    const matchupIds = [...new Set(sabs.filter(s => s.matchup_id).map(s => s.matchup_id!))];
    const { data: matchups } = matchupIds.length > 0
      ? await supabase.from('matchups').select('id, home_team_name, away_team_name, week').in('id', matchupIds)
      : { data: [] };
    const matchupMap = new Map((matchups || []).map(m => [m.id, `Wk${m.week}: ${m.home_team_name} vs ${m.away_team_name}`]));

    const rows: SabotageRow[] = sabs.map(s => ({
      id: s.id,
      leagueName: leagueMap.get(s.league_id) || 'Unknown',
      matchupLabel: s.matchup_id ? (matchupMap.get(s.matchup_id) || s.matchup_id.slice(0, 8)) : '-',
      sabotage_name: s.sabotage_name,
      rarity: sabotageRarityMap.get(s.sabotage_name) || 'unknown',
      effect_config: s.effect_config,
      appliedBy: teamMap.get(s.applied_by_team_id) || 'Unknown',
      applied_at: s.applied_at,
      week: s.week,
      league_id: s.league_id,
    }));
    setSabotages(rows);
  };

  // ─── 3b. OUTLIER SCORES ───────────────────────────────────
  const fetchOutliers = async () => {
    const { data: leagues } = await supabase.from('leagues').select('id, name');
    if (!leagues) return;
    const leagueMap = new Map(leagues.map(l => [l.id, l.name]));

    // Get this week's Monday (ET)
    const weekStart = etWeekStartMonday();   // SAB-793

    // Fetch extreme scores this week (top positive and top negative)
    const [highRes, lowRes] = await Promise.all([
      supabase.from('player_match_scores')
        .select('player_id, team_id, league_id, game_date, total_points')
        .gte('game_date', weekStart)
        .order('total_points', { ascending: false })
        .limit(10),
      supabase.from('player_match_scores')
        .select('player_id, team_id, league_id, game_date, total_points')
        .gte('game_date', weekStart)
        .order('total_points', { ascending: true })
        .limit(10),
    ]);

    const allRows = [...(highRes.data || []), ...(lowRes.data || [])];
    // Filter to only real outliers: |score| > 50
    const outlierRows = allRows.filter(r => Math.abs(Number(r.total_points)) > 50);
    if (outlierRows.length === 0) { setOutliers([]); return; }

    // Lookup player names
    const playerIds = [...new Set(outlierRows.map(r => r.player_id))];
    const { data: players } = await supabase.from('players').select('id, full_name').in('id', playerIds);
    const playerMap = new Map((players || []).map(p => [p.id, p.full_name]));

    // Lookup team names
    const teamIds = [...new Set(outlierRows.map(r => r.team_id).filter(Boolean))];
    const { data: teams } = teamIds.length > 0
      ? await supabase.from('teams').select('id, name').in('id', teamIds)
      : { data: [] };
    const teamMap = new Map((teams || []).map(t => [t.id, t.name]));

    const rows: OutlierRow[] = outlierRows
      .sort((a, b) => Math.abs(Number(b.total_points)) - Math.abs(Number(a.total_points)))
      .map(r => ({
        playerName: playerMap.get(r.player_id) || 'Unknown',
        teamName: teamMap.get(r.team_id) || '-',
        leagueName: leagueMap.get(r.league_id) || 'Unknown',
        gameDate: r.game_date,
        totalPoints: Number(r.total_points),
        playerId: r.player_id,
      }));
    setOutliers(rows);
  };

  // ─── 4. CRON HEALTH + IMPACT ──────────────────────────────
  const fetchCronHealth = async () => {
    // SAB-793: named todayETStr, NOT todayET — a local of that name shadows the imported
    // todayET() function, and the call below then tries to invoke a string.
    const todayETStr = todayET();

    const [boxRes, matchupRes, seasonRes, boxTodayRes, liveOrFinalRes, gamesTodayRes] = await Promise.all([
      supabase.from('player_game_stats').select('updated_at').order('updated_at', { ascending: false }).limit(1),
      supabase.from('player_match_scores').select('scored_at').order('scored_at', { ascending: false }).limit(1),
      supabase.from('player_season_stats').select('updated_at').order('updated_at', { ascending: false }).limit(1),
      supabase.from('player_game_stats').select('id', { count: 'exact', head: true }).eq('game_date', todayETStr),
      supabase.from('game_schedule').select('id', { count: 'exact', head: true }).eq('game_date', todayETStr).in('status', ['live', 'final']),
      supabase.from('game_schedule').select('id', { count: 'exact', head: true }).eq('game_date', todayETStr),
    ]);

    const boxTs = boxRes.data?.[0]?.updated_at || null;
    const matchupTs = matchupRes.data?.[0]?.scored_at || null;
    const seasonTs = seasonRes.data?.[0]?.updated_at || null;
    const boxScoresTodayCount = boxTodayRes.count || 0;
    const liveOrFinalCount = liveOrFinalRes.count || 0;
    const gamesTodayCount = gamesTodayRes.count || 0;

    setCronHealth({ boxScoresLastRun: boxTs, boxScoresTodayCount, gamesTodayCount, liveOrFinalCount, matchupEngineLastRun: matchupTs, seasonStatsLastRun: seasonTs });

    // Compute impact: how many games are live/final but not yet fetched since the cron went down
    if (isStale(boxTs, 10)) {
      const td = todayStr();
      const yesterdayStr = addDaysToBareDate(todayETStr, -1);   // SAB-793

      // Count games that are live/final for today and yesterday
      const { count: unscoredGames } = await supabase
        .from('game_schedule')
        .select('id', { count: 'exact', head: true })
        .in('game_date', [yesterdayStr, td])
        .in('status', ['live', 'final']);

      // Count active matchups across all leagues
      const { count: affectedMatchups } = await supabase
        .from('matchups')
        .select('id', { count: 'exact', head: true })
        .in('status', ['in_progress', 'upcoming']);

      setCronImpact({
        unscoredGames: unscoredGames || 0,
        affectedMatchups: affectedMatchups || 0,
      });
    } else {
      setCronImpact({ unscoredGames: 0, affectedMatchups: 0 });
    }
  };

  // ─── 5. PLAYER CREDITS ────────────────────────────────────
  const fetchCredits = async () => {
    // Get all teams with user_id and league info
    const { data: allTeams } = await supabase.from('teams').select('user_id, name, league_id');
    if (!allTeams) return;
    const userIds = [...new Set(allTeams.map(t => t.user_id).filter(Boolean))];
    if (userIds.length === 0) { setCreditRows([]); return; }

    const { data: leagues } = await supabase.from('leagues').select('id, name');
    const leagueMap = new Map((leagues || []).map(l => [l.id, l.name]));

    // Build user → team+league mapping (first team found)
    const userTeamMap = new Map<string, { teamName: string; leagueName: string }>();
    for (const t of allTeams) {
      if (t.user_id && !userTeamMap.has(t.user_id)) {
        userTeamMap.set(t.user_id, {
          teamName: t.name,
          leagueName: leagueMap.get(t.league_id) || 'Unknown',
        });
      }
    }

    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, username, display_name' as any)
      .in('id', userIds)
      .eq('beta_credits_granted' as any, false);

    if (!profiles || profiles.length === 0) { setCreditRows([]); return; }

    const rows: CreditRow[] = (profiles as any[]).map(p => {
      const team = userTeamMap.get(p.id);
      return {
        userId: p.id,
        username: p.username,
        displayName: p.display_name,
        teamName: team?.teamName || '-',
        leagueName: team?.leagueName || '-',
      };
    });
    setCreditRows(rows);
  };

  const grantCredits = async (userId: string) => {
    setGrantingId(userId);
    const { error } = await supabase
      .from('profiles')
      .update({
        draft_sabotage_credits: 325,
        matchup_sabotage_credits: 0,
        beta_credits_granted: true,
      } as any)
      .eq('id', userId);
    if (!error) {
      setCreditRows(prev => prev.filter(r => r.userId !== userId));
    }
    setGrantingId(null);
  };

  // ─── 7. CLIENT ERRORS ──────────────────────────────────────
  const fetchClientErrors = async () => {
    // Last 48 hours of errors
    const since = new Date(Date.now() - 48 * 3_600_000).toISOString();
    const { data: errors } = await supabase
      .from('client_errors')
      .select('id, user_id, message, page, component, metadata, created_at')
      .gte('created_at', since)
      .order('created_at', { ascending: false })
      .limit(200);

    if (!errors || errors.length === 0) { setClientErrors([]); return; }

    const userIds = [...new Set(errors.map(e => e.user_id).filter(Boolean))] as string[];
    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, username, display_name' as any)
      .in('id', userIds);

    const profileMap = new Map<string, { username: string | null; displayName: string | null }>();
    (profiles as any[] || []).forEach((p: any) => {
      profileMap.set(p.id, { username: p.username, displayName: p.display_name });
    });

    const rows: ClientErrorRow[] = errors.map(e => {
      const profile = e.user_id ? profileMap.get(e.user_id) : null;
      return {
        id: e.id,
        message: e.message,
        page: e.page,
        component: e.component,
        username: profile?.username || null,
        displayName: profile?.displayName || null,
        createdAt: e.created_at,
        metadata: e.metadata,
      };
    });
    setClientErrors(rows);
  };

  // ─── Auto-refresh ──────────────────────────────────────────
  useEffect(() => {
    if (isAdmin) fetchAll();
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) return;
    intervalRef.current = setInterval(fetchAll, 60_000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [isAdmin, fetchAll]);

  // ─── Auth Gate ─────────────────────────────────────────────
  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Shield className="w-12 h-12 text-primary mx-auto animate-pulse" />
      </div>
    );
  }

  if (!user || !isAdmin) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md w-full border-destructive/30">
          <CardContent className="p-8 text-center space-y-4">
            <Shield className="w-16 h-16 text-destructive mx-auto" />
            <h2 className="text-xl font-display font-bold">Access Denied</h2>
            <p className="text-muted-foreground">Admin access required.</p>
            <Button variant="outline" onClick={() => navigate('/beta')}>
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to App
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ─── Sabotage flag helpers ─────────────────────────────────
  const legendaryByMatchup = new Map<string, number>();
  sabotages.forEach(s => {
    if (s.rarity === 'legendary' && s.matchupLabel !== '-') {
      const key = `${s.league_id}_${s.matchupLabel}`;
      legendaryByMatchup.set(key, (legendaryByMatchup.get(key) || 0) + 1);
    }
  });

  // Build duplicate counts: league+week+name → count
  const sabDupCounts = new Map<string, { count: number; teams: string[] }>();
  sabotages.forEach(s => {
    const key = `${s.league_id}_${s.week}_${s.sabotage_name}`;
    const entry = sabDupCounts.get(key) || { count: 0, teams: [] };
    entry.count++;
    entry.teams.push(s.appliedBy);
    sabDupCounts.set(key, entry);
  });

  // Error counts for tab badges
  const discrepancyCount = matchupScores.filter(m => m.discrepancy).length;
  const dupSabCount = [...sabDupCounts.values()].filter(v => v.count > 1).length;
  const boxScoresUnhealthy = isStale(cronHealth.boxScoresLastRun, 10) || (cronHealth.liveOrFinalCount > 0 && cronHealth.boxScoresTodayCount === 0);
  const matchupEngineUnhealthy = isStale(cronHealth.matchupEngineLastRun, 10) && cronHealth.liveOrFinalCount > 0;
  const cronStaleCount = [
    boxScoresUnhealthy,
    matchupEngineUnhealthy,
    isStale(cronHealth.seasonStatsLastRun, 1440),
  ].filter(Boolean).length;
  const totalErrors = discrepancyCount + dupSabCount + cronStaleCount + creditRows.length + outliers.length + clientErrors.length;

  // ─── Render ────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-background">
      <div className="border-b border-border bg-card/50 sticky top-0 z-50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/20">
              <Eye className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h1 className="font-display font-bold text-lg flex items-center gap-2">
                Ops Dashboard
                <Badge className="bg-primary/20 text-primary border-primary/30 text-xs">ADMIN</Badge>
                {totalErrors > 0 && (
                  <Badge variant="outline" className="text-red-500 border-red-500/40 bg-red-500/10 text-xs">
                    {totalErrors} issue{totalErrors !== 1 ? 's' : ''}
                  </Badge>
                )}
              </h1>
              <p className="text-xs text-muted-foreground">
                Last refresh: {lastRefresh.toLocaleTimeString()} · Auto-refreshes every 60s
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={fetchAll} disabled={loading}>
              <RefreshCw className={cn("w-4 h-4", loading && "animate-spin")} />
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate('/admin/god-mode')}>
              <Eye className="w-4 h-4 mr-1" /> God Mode
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate('/beta')}>
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {loading && scoringHealth.length === 0 ? (
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-32 w-full" />)}
          </div>
        ) : (
          <Tabs defaultValue="scoring" className="space-y-4">
            <TabsList className="bg-muted/50 flex flex-wrap h-auto gap-1">
              <TabsTrigger value="scoring" className="text-xs"><HeartPulse className="w-3 h-3 mr-1" />Scoring</TabsTrigger>
              <TabsTrigger value="matchups" className="text-xs">
                <Activity className="w-3 h-3 mr-1" />Matchups
                {discrepancyCount > 0 && <Badge variant="outline" className="ml-1 text-[9px] px-1 py-0 h-4 text-red-500 border-red-500/40">{discrepancyCount}</Badge>}
              </TabsTrigger>
              <TabsTrigger value="sabotages" className="text-xs">
                <Bomb className="w-3 h-3 mr-1" />Sabotages
                {dupSabCount > 0 && <Badge variant="outline" className="ml-1 text-[9px] px-1 py-0 h-4 text-red-500 border-red-500/40">{dupSabCount}</Badge>}
              </TabsTrigger>
              <TabsTrigger value="outliers" className="text-xs">
                <TrendingUp className="w-3 h-3 mr-1" />Outliers
                {outliers.length > 0 && <Badge variant="outline" className="ml-1 text-[9px] px-1 py-0 h-4 text-orange-500 border-orange-500/40">{outliers.length}</Badge>}
              </TabsTrigger>
              <TabsTrigger value="crons" className="text-xs">
                <Clock className="w-3 h-3 mr-1" />Crons
                {cronStaleCount > 0 && <Badge variant="outline" className="ml-1 text-[9px] px-1 py-0 h-4 text-red-500 border-red-500/40">{cronStaleCount}</Badge>}
              </TabsTrigger>
              <TabsTrigger value="credits" className="text-xs">
                <CreditCard className="w-3 h-3 mr-1" />Credits
                {creditRows.length > 0 && <Badge variant="outline" className="ml-1 text-[9px] px-1 py-0 h-4 text-orange-500 border-orange-500/40">{creditRows.length}</Badge>}
              </TabsTrigger>
              <TabsTrigger value="errors" className="text-xs">
                <MessageSquareWarning className="w-3 h-3 mr-1" />Errors
                {clientErrors.length > 0 && <Badge variant="outline" className="ml-1 text-[9px] px-1 py-0 h-4 text-red-500 border-red-500/40">{clientErrors.length}</Badge>}
              </TabsTrigger>
            </TabsList>

            {/* ─── 1. SCORING HEALTH ─── */}
            <TabsContent value="scoring">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <HeartPulse className="w-4 h-4" /> Scoring Health
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border text-xs text-muted-foreground">
                          <th className="text-left py-2 px-3">League</th>
                          <th className="text-left py-2 px-3">Last Scored</th>
                          <th className="text-right py-2 px-3">Today</th>
                          <th className="text-right py-2 px-3">This Week</th>
                          <th className="text-right py-2 px-3">Duplicates</th>
                        </tr>
                      </thead>
                      <tbody>
                        {scoringHealth.map(league => {
                          const stale = isStale(league.lastScoredAt, 10);
                          return (
                            <tr key={league.id} className="border-b border-border/30">
                              <td className="py-2 px-3 font-medium">{league.name}</td>
                              <td className={cn("py-2 px-3", stale && "text-red-500 font-semibold")}>
                                {league.lastScoredAt ? (
                                  <span className="flex items-center gap-1">
                                    {stale && <AlertTriangle className="w-3 h-3" />}
                                    {timeAgo(league.lastScoredAt)}
                                  </span>
                                ) : 'never'}
                              </td>
                              <td className="py-2 px-3 text-right">{league.rowsToday}</td>
                              <td className="py-2 px-3 text-right">{league.rowsThisWeek}</td>
                              <td className={cn("py-2 px-3 text-right", league.duplicates > 0 && "text-red-500 font-semibold")}>
                                {league.duplicates > 0 ? (
                                  <span className="flex items-center justify-end gap-1"><AlertTriangle className="w-3 h-3" />{league.duplicates}</span>
                                ) : (
                                  <span className="flex items-center justify-end gap-1 text-green-500"><CheckCircle2 className="w-3 h-3" /> 0</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* ─── 2. MATCHUP SCORES ─── */}
            <TabsContent value="matchups">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Activity className="w-4 h-4" /> Matchup Scores: Week 1
                    {discrepancyCount > 0 && (
                      <Badge variant="outline" className="text-red-500 border-red-500/40 bg-red-500/10 text-xs">
                        <AlertTriangle className="w-3 h-3 mr-1" /> {discrepancyCount} discrepanc{discrepancyCount === 1 ? 'y' : 'ies'}
                      </Badge>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[500px]">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-border text-xs text-muted-foreground">
                            <th className="text-left py-2 px-2">League</th>
                            <th className="text-left py-2 px-2">Matchup ID</th>
                            <th className="text-left py-2 px-2">Home</th>
                            <th className="text-right py-2 px-2">Stored</th>
                            <th className="text-right py-2 px-2">Calc</th>
                            <th className="text-right py-2 px-2">Diff</th>
                            <th className="text-center py-2 px-2">vs</th>
                            <th className="text-left py-2 px-2">Away</th>
                            <th className="text-right py-2 px-2">Stored</th>
                            <th className="text-right py-2 px-2">Calc</th>
                            <th className="text-right py-2 px-2">Diff</th>
                            <th className="text-left py-2 px-2">Affected</th>
                          </tr>
                        </thead>
                        <tbody>
                          {matchupScores.map(m => {
                            const homeFlag = Math.abs(m.homeDiff) > 0.01;
                            const awayFlag = Math.abs(m.awayDiff) > 0.01;
                            return (
                              <tr key={m.id} className={cn("border-b border-border/30", m.discrepancy && "bg-red-500/5")}>
                                <td className="py-2 px-2 text-xs text-muted-foreground">{m.leagueName}</td>
                                <td className="py-2 px-2 text-[10px] font-mono text-muted-foreground">{m.id.slice(0, 8)}</td>
                                <td className={cn("py-2 px-2 font-medium truncate max-w-[100px]", homeFlag && "text-red-500")}>{m.homeTeam}</td>
                                <td className="py-2 px-2 text-right">{m.homeScore.toFixed(1)}</td>
                                <td className="py-2 px-2 text-right text-muted-foreground">{m.homeCalc.toFixed(1)}</td>
                                <td className={cn("py-2 px-2 text-right font-mono", homeFlag ? "text-red-500 font-semibold" : "text-muted-foreground/50")}>
                                  {m.homeDiff >= 0 ? '+' : ''}{m.homeDiff.toFixed(1)}
                                </td>
                                <td className="py-2 px-2 text-center text-muted-foreground">vs</td>
                                <td className={cn("py-2 px-2 font-medium truncate max-w-[100px]", awayFlag && "text-red-500")}>{m.awayTeam}</td>
                                <td className="py-2 px-2 text-right">{m.awayScore.toFixed(1)}</td>
                                <td className="py-2 px-2 text-right text-muted-foreground">{m.awayCalc.toFixed(1)}</td>
                                <td className={cn("py-2 px-2 text-right font-mono", awayFlag ? "text-red-500 font-semibold" : "text-muted-foreground/50")}>
                                  {m.awayDiff >= 0 ? '+' : ''}{m.awayDiff.toFixed(1)}
                                </td>
                                <td className="py-2 px-2 text-xs">
                                  {m.affectedSide === 'none' ? (
                                    <span className="text-green-500">OK</span>
                                  ) : (
                                    <Badge variant="outline" className="text-[10px] text-red-500 border-red-500/40 bg-red-500/10">
                                      {m.affectedSide === 'both' ? 'Both teams' : m.affectedSide === 'home' ? m.homeTeam : m.awayTeam}
                                    </Badge>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                          {matchupScores.length === 0 && (
                            <tr><td colSpan={12} className="text-center py-8 text-muted-foreground">No week 1 matchups found</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            </TabsContent>

            {/* ─── 3. SABOTAGE STATE ─── */}
            <TabsContent value="sabotages">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Bomb className="w-4 h-4" /> Active Sabotages ({sabotages.length})
                    {dupSabCount > 0 && (
                      <Badge variant="outline" className="text-red-500 border-red-500/40 bg-red-500/10 text-xs">
                        <AlertTriangle className="w-3 h-3 mr-1" /> {dupSabCount} duplicate{dupSabCount !== 1 ? 's' : ''}
                      </Badge>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {sabotages.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">No active sabotages</p>
                  ) : (
                    <ScrollArea className="h-[500px]">
                      <div className="space-y-2">
                        {sabotages.map(s => {
                          const matchupKey = `${s.league_id}_${s.matchupLabel}`;
                          const tooManyLegendary = s.rarity === 'legendary' && (legendaryByMatchup.get(matchupKey) || 0) > 1;
                          const dupKey = `${s.league_id}_${s.week}_${s.sabotage_name}`;
                          const dupInfo = sabDupCounts.get(dupKey);
                          const isDup = dupInfo && dupInfo.count > 1;
                          const flagged = tooManyLegendary || isDup;

                          return (
                            <div key={s.id} className={cn("p-3 rounded-lg text-sm", flagged ? "bg-red-500/10 border border-red-500/30" : "bg-muted/20")}>
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-medium">{s.sabotage_name}</span>
                                <Badge variant="outline" className={cn("text-[10px]",
                                  s.rarity === 'legendary' ? 'text-purple-400 border-purple-400/40 bg-purple-400/10' :
                                  s.rarity === 'rare' ? 'text-blue-400 border-blue-400/40 bg-blue-400/10' :
                                  'text-muted-foreground'
                                )}>{s.rarity}</Badge>
                                {tooManyLegendary && (
                                  <Badge variant="outline" className="text-[10px] text-red-500 border-red-500/40 bg-red-500/10">
                                    <AlertTriangle className="w-3 h-3 mr-1" /> {legendaryByMatchup.get(matchupKey)} legendary in matchup!
                                  </Badge>
                                )}
                                {isDup && (
                                  <Badge variant="outline" className="text-[10px] text-red-500 border-red-500/40 bg-red-500/10">
                                    <AlertTriangle className="w-3 h-3 mr-1" /> {dupInfo.count}x in {s.leagueName} Wk{s.week}, teams: {dupInfo.teams.join(', ')}
                                  </Badge>
                                )}
                              </div>
                              <div className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-x-3">
                                <span>{s.leagueName}</span>
                                <span>{s.matchupLabel}</span>
                                <span>Applied by: <strong>{s.appliedBy}</strong></span>
                                <span>{timeAgo(s.applied_at)}</span>
                              </div>
                              <div className="text-[10px] text-muted-foreground/60 mt-0.5 font-mono">{JSON.stringify(s.effect_config)}</div>
                            </div>
                          );
                        })}
                      </div>
                    </ScrollArea>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* ─── 3b. OUTLIER SCORES ─── */}
            <TabsContent value="outliers">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" /> Score Outliers This Week (|pts| &gt; 50)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {outliers.length === 0 ? (
                    <div className="text-center py-8 space-y-2">
                      <CheckCircle2 className="w-8 h-8 text-green-500 mx-auto" />
                      <p className="text-muted-foreground">No outlier scores detected this week</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-border text-xs text-muted-foreground">
                            <th className="text-left py-2 px-3">Player</th>
                            <th className="text-left py-2 px-3">Team</th>
                            <th className="text-left py-2 px-3">League</th>
                            <th className="text-left py-2 px-3">Date</th>
                            <th className="text-right py-2 px-3">Score</th>
                          </tr>
                        </thead>
                        <tbody>
                          {outliers.map((o, i) => (
                            <tr key={`${o.playerId}_${o.gameDate}_${i}`} className={cn("border-b border-border/30",
                              Math.abs(o.totalPoints) > 80 ? "bg-orange-500/10" : "bg-muted/5"
                            )}>
                              <td className="py-2 px-3 font-medium">{o.playerName}</td>
                              <td className="py-2 px-3 text-muted-foreground">{o.teamName}</td>
                              <td className="py-2 px-3 text-muted-foreground">{o.leagueName}</td>
                              <td className="py-2 px-3 text-muted-foreground">{o.gameDate}</td>
                              <td className={cn("py-2 px-3 text-right font-bold",
                                o.totalPoints > 0 ? "text-orange-500" : "text-blue-500"
                              )}>{o.totalPoints.toFixed(1)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* ─── 4. CRON HEALTH ─── */}
            <TabsContent value="crons">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Clock className="w-4 h-4" /> Cron Health
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {[
                      { label: 'fetch-box-scores', ts: cronHealth.boxScoresLastRun, threshold: 10, showImpact: true, overrideStale: boxScoresUnhealthy },
                      { label: 'matchup-engine', ts: cronHealth.matchupEngineLastRun, threshold: 10, showImpact: false, overrideStale: matchupEngineUnhealthy },
                      { label: 'fetch-season-stats', ts: cronHealth.seasonStatsLastRun, threshold: 1440, showImpact: false },
                    ].map(cron => {
                      const stale = cron.overrideStale ?? isStale(cron.ts, cron.threshold);
                      const hrs = hoursDown(cron.ts);
                      return (
                        <div key={cron.label} className={cn("p-4 rounded-lg", stale ? "bg-red-500/10 border border-red-500/30" : "bg-muted/20")}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              {stale ? <AlertTriangle className="w-5 h-5 text-red-500" /> : <CheckCircle2 className="w-5 h-5 text-green-500" />}
                              <div>
                                <p className="font-medium text-sm">{cron.label}</p>
                                <p className="text-xs text-muted-foreground">
                                  Threshold: {cron.threshold >= 1440 ? '24h' : `${cron.threshold}m`}
                                </p>
                                {cron.label === 'fetch-box-scores' && (
                                  <p className="text-xs text-muted-foreground">
                                    Today: {cronHealth.boxScoresTodayCount} stat rows / {cronHealth.liveOrFinalCount} live/final of {cronHealth.gamesTodayCount} scheduled
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="text-right">
                              <p className={cn("text-sm font-medium", stale && "text-red-500")}>
                                {cron.ts ? timeAgo(cron.ts) : 'never'}
                              </p>
                              {cron.ts && <p className="text-[10px] text-muted-foreground">{new Date(cron.ts).toLocaleString()}</p>}
                            </div>
                          </div>
                          {stale && cron.label === 'fetch-box-scores' && cronHealth.gamesTodayCount > 0 && cronHealth.boxScoresTodayCount === 0 && (
                            <div className="mt-3 p-2 rounded bg-red-500/10 border border-red-500/20">
                              <p className="text-xs text-red-400 font-medium">
                                {cronHealth.liveOrFinalCount} live/final game{cronHealth.liveOrFinalCount !== 1 ? 's' : ''} today but 0 player_game_stats rows. Cron may not be running
                              </p>
                            </div>
                          )}
                          {stale && cron.showImpact && (cronImpact.unscoredGames > 0 || cronImpact.affectedMatchups > 0) && (
                            <div className="mt-3 p-2 rounded bg-red-500/10 border border-red-500/20">
                              <p className="text-xs text-red-400 font-medium">
                                Estimated impact ({hrs}h down):
                              </p>
                              <ul className="text-xs text-red-400/80 mt-1 space-y-0.5 pl-4 list-disc">
                                <li>{cronImpact.unscoredGames} live/final game{cronImpact.unscoredGames !== 1 ? 's' : ''} may have stale box scores</li>
                                <li>{cronImpact.affectedMatchups} active matchup{cronImpact.affectedMatchups !== 1 ? 's' : ''} with potentially outdated scores</li>
                              </ul>
                            </div>
                          )}
                          {stale && cron.label === 'matchup-engine' && cronHealth.liveOrFinalCount > 0 && (
                            <div className="mt-3 p-2 rounded bg-red-500/10 border border-red-500/20">
                              <p className="text-xs text-red-400">
                                player_match_scores not being written. Matchup totals and live scores are stale for all leagues
                              </p>
                            </div>
                          )}
                          {stale && cron.label === 'matchup-engine' && cronHealth.liveOrFinalCount === 0 && (
                            <div className="mt-3 p-2 rounded bg-yellow-500/10 border border-yellow-500/20">
                              <p className="text-xs text-yellow-400">
                                No live/final games right now ({cronHealth.gamesTodayCount} scheduled). Matchup engine has nothing to score
                              </p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* ─── 5. PLAYER CREDITS ─── */}
            <TabsContent value="credits">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <CreditCard className="w-4 h-4" /> Ungranted Beta Credits ({creditRows.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {creditRows.length === 0 ? (
                    <div className="text-center py-8 space-y-2">
                      <CheckCircle2 className="w-8 h-8 text-green-500 mx-auto" />
                      <p className="text-muted-foreground">All league members have been granted credits</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-border text-xs text-muted-foreground">
                            <th className="text-left py-2 px-3">User</th>
                            <th className="text-left py-2 px-3">Team</th>
                            <th className="text-left py-2 px-3">League</th>
                            <th className="text-left py-2 px-3">User ID</th>
                            <th className="text-right py-2 px-3">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {creditRows.map(row => (
                            <tr key={row.userId} className="border-b border-border/30">
                              <td className="py-2 px-3 font-medium">{row.displayName || row.username || 'Unknown'}</td>
                              <td className="py-2 px-3 text-muted-foreground">{row.teamName}</td>
                              <td className="py-2 px-3 text-muted-foreground">{row.leagueName}</td>
                              <td className="py-2 px-3 text-[10px] font-mono text-muted-foreground">{row.userId.slice(0, 12)}...</td>
                              <td className="py-2 px-3 text-right">
                                <Button
                                  size="sm" variant="outline"
                                  className="text-xs border-green-500/40 text-green-500 hover:bg-green-500/10"
                                  disabled={grantingId === row.userId}
                                  onClick={() => grantCredits(row.userId)}
                                >
                                  {grantingId === row.userId ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : <CreditCard className="w-3 h-3 mr-1" />}
                                  Grant 325
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
            {/* ─── 7. CLIENT ERRORS ─── */}
            <TabsContent value="errors">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <MessageSquareWarning className="w-4 h-4" /> Client Errors: Last 48h ({clientErrors.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {clientErrors.length === 0 ? (
                    <div className="text-center py-8 space-y-2">
                      <CheckCircle2 className="w-8 h-8 text-green-500 mx-auto" />
                      <p className="text-muted-foreground">No client errors in the last 48 hours</p>
                    </div>
                  ) : (
                    <ScrollArea className="h-[500px]">
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-border text-xs text-muted-foreground">
                              <th className="text-left py-2 px-3">When</th>
                              <th className="text-left py-2 px-3">User</th>
                              <th className="text-left py-2 px-3">Error</th>
                              <th className="text-left py-2 px-3">Page</th>
                            </tr>
                          </thead>
                          <tbody>
                            {clientErrors.map(err => (
                              <tr key={err.id} className="border-b border-border/30">
                                <td className="py-2 px-3 text-xs text-muted-foreground whitespace-nowrap">
                                  {timeAgo(err.createdAt)}
                                </td>
                                <td className="py-2 px-3 text-xs font-medium whitespace-nowrap">
                                  {err.displayName || err.username || '-'}
                                </td>
                                <td className="py-2 px-3">
                                  <span className="text-red-400 text-xs">{err.message}</span>
                                  {err.metadata && Object.keys(err.metadata).length > 0 && (
                                    <span className="block text-[10px] text-muted-foreground/60 font-mono mt-0.5">
                                      {JSON.stringify(err.metadata)}
                                    </span>
                                  )}
                                </td>
                                <td className="py-2 px-3 text-[10px] text-muted-foreground font-mono whitespace-nowrap">
                                  {err.page || '-'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </ScrollArea>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
